import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  dateFromString,
  dateToMonth,
  dateToString,
  forwardDays,
  forwardMonths,
  getStartOfMonth,
  isSameMonth,
  monthToString
} from '../../utils/date'
import { useNotification } from '../../context/NotificationManager/NotificationManager'
import { Button, IconButton, Typography } from '@mui/material'
import Calendar from '../../components/Calendar/Calendar'
import { createClases, getClasesFechas } from '../../utils/api/clases'
import { mergeClases } from '../../utils/model/horario'
import { getAuth } from '../../utils/auth'
import { canRequestProfesores } from '../../utils/security'
import ModalPlanificarClasesProfesor from '../../modals/ModalPlanificarClasesProfesor/ModalPlanificarClasesProfesor'
import css from './Profesores.module.css'

const ClaseCalendario = ({ hora, profesor, dimmed }) => (
  <div className={css.calendarEvent} data-status={dimmed ? 'dimmed' : 'normal'}>
    <p className={css.calendarEventRow}>
      <i className='material-icons'>person</i> {profesor}
    </p>
    <p className={css.calendarEventRow}>
      <i className='material-icons'>schedule</i> {hora}
    </p>
  </div>
)

const Profesores = () => {
  const notification = useNotification()
  const queryClient = useQueryClient()
  const userData = getAuth()

  const [mes, setMes] = useState(getStartOfMonth())
  const [isAddModalOpen, setAddModalOpen] = useState(false)

  const handleMonthChange = offset => {
    const newMes = new Date(mes)
    newMes.setMonth(newMes.getMonth() + offset)
    setMes(newMes)
  }

  const { data: clasesList = [] } = useQuery({
    queryKey: ['planificacion', 'clases-profesor', 'list', monthToString(mes)],
    queryFn: () =>
      getClasesFechas({
        fechaInicio: dateToString(forwardDays(mes, -6)),
        fechaFin: dateToString(forwardDays(forwardMonths(mes, 1), 5))
      })
        .then(datos =>
          datos.map(clase => ({
            ...clase,
            fecha: dateToString(clase.fecha)
          }))
        )
        .catch(err => {
          notification.error({
            title: 'Error al recuperar las clases',
            content: err
          })
          return []
        })
  })

  const { isPending: isClasesPlanning, mutate: crearClases } = useMutation({
    mutationFn: createClases,
    onSuccess: () => {
      notification.success({ title: 'Clases planificadas correctamente' })
      queryClient.invalidateQueries([
        'planificacion',
        'clases-profesor',
        'list',
        monthToString(mes)
      ])
      setAddModalOpen(false)
    },
    onError: err =>
      notification.error({
        title: 'Error al planificar las clases',
        content: err
      })
  })

  const clasesCalendario = mergeClases(clasesList)
    .map(clasesDia => ({
      fecha: clasesDia.fecha,
      clases: clasesDia.clases.map(clase => (
        <ClaseCalendario
          key={clase.id}
          hora={clase.hora}
          profesor={clase.profesor}
          dimmed={!isSameMonth(dateFromString(clasesDia.fecha), mes)}
        />
      ))
    }))
    .reduce(
      (acc, clasesDia) => ({
        ...acc,
        [clasesDia.fecha]: clasesDia.clases
      }),
      {}
    )

  const handleAddClases = ({ profesor, horas }) => {
    if (isClasesPlanning) return
    crearClases({ profesor, asistencias: horas })
  }

  const showAddButton = canRequestProfesores(userData)

  return (
    <div className={css.main}>
      <div className={css.header}>
        <Typography variant='h6'>Profesores disponibles</Typography>
        {showAddButton && (
          <Button
            variant='outlined'
            color='primary'
            startIcon={<i className='material-icons'>add</i>}
            onClick={() => setAddModalOpen(true)}
          >
            Añadir clases
          </Button>
        )}
      </div>
      <div className={css.calendar}>
        <div className={css.monthSelection}>
          <IconButton
            className={css.monthIcon}
            size='small'
            onClick={() => handleMonthChange(-1)}
          >
            <i className='material-icons'>arrow_back</i>
          </IconButton>
          <p className={css.calendarMonth}>{dateToMonth(mes)}</p>
          <IconButton
            className={css.monthIcon}
            size='small'
            onClick={() => handleMonthChange(+1)}
          >
            <i className='material-icons'>arrow_forward</i>
          </IconButton>
        </div>
        <Calendar month={monthToString(mes)} events={clasesCalendario} />
      </div>
      {isAddModalOpen && (
        <ModalPlanificarClasesProfesor
          open={isAddModalOpen}
          mes={mes}
          onClose={() => setAddModalOpen(false)}
          onSubmit={handleAddClases}
        />
      )}
    </div>
  )
}

export default Profesores
